import React, { useEffect, useState, useCallback } from "react";
import { db, auth } from "./firebase";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where
} from "firebase/firestore";
import { 
  getStorage,
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from "firebase/storage";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./navbar-spacing.css";
import "./GestioneBannerPromozionale.css";

function GestioneBannerPromozionale() {
  const [banners, setBanners] = useState([]);
  const [localeTag, setLocaleTag] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState("");
  
  // Form per nuovo banner
  const [nuovoTitolo, setNuovoTitolo] = useState("");
  const [nuovoLink, setNuovoLink] = useState("");
  const [nuovaImmagine, setNuovaImmagine] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formKey, setFormKey] = useState(0);
  
  // Modalità modifica
  const [modalitaModifica, setModalitaModifica] = useState(false);
  const [bannerInModifica, setBannerInModifica] = useState(null);

  const navigate = useNavigate();

  // Normalizza localeTag
  const normalizzaLocaleTag = (tag) => {
    return tag.toLowerCase().replace(/[^a-z0-9]/gi, "");
  };

  // Controllo autenticazione e ruolo gestore
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }
      
      const utenteRef = doc(db, "utenti", user.uid);
      const utenteSnap = await getDoc(utenteRef);
      
      if (!utenteSnap.exists()) {
        navigate("/login");
        return;
      }
      
      const userData = utenteSnap.data();
      
      // Solo gestore può accedere
      if (userData.ruolo !== "gestore") {
        navigate("/dashboard-gestore");
        return;
      }
      
      setLocaleTag(normalizzaLocaleTag(userData.localeTag || ""));
    });
    
    return () => unsubscribe();
  }, [navigate]);

  // Ricarica banner
  const ricaricaBanners = useCallback(async () => {
    if (!localeTag) return;
    
    setLoading(true);
    try {
      const q = query(
        collection(db, "banners_promozionali"),
        where("localeTag", "==", localeTag)
      );
      const snapshot = await getDocs(q);
      
      const lista = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
      
      setBanners(lista);
    } catch (err) {
      console.error("Errore nel caricamento banner:", err);
    }
    setLoading(false);
  }, [localeTag]);

  useEffect(() => {
    ricaricaBanners();
  }, [ricaricaBanners]);

  // Carica nuovo banner
  const caricaBanner = async (e) => {
    e.preventDefault();
    
    if (!localeTag || localeTag.trim() === "") {
      setSnackbar("❌ Errore: Locale non identificato.");
      setTimeout(() => setSnackbar(""), 3000);
      return;
    }
    
    if (!nuovoTitolo.trim()) {
      setSnackbar("⚠️ Inserisci un titolo!");
      setTimeout(() => setSnackbar(""), 3000);
      return;
    }
    
    if (!nuovaImmagine) {
      setSnackbar("⚠️ Seleziona un'immagine!");
      setTimeout(() => setSnackbar(""), 3000);
      return;
    }
    
    setUploading(true);
    
    try {
      const storage = getStorage();
      const filename = nuovaImmagine.name;
      const timestamp = Date.now();
      const filePath = `banners_promozionali/${localeTag}_${timestamp}_${filename}`;
      const fileRef = ref(storage, filePath);
      
      await uploadBytes(fileRef, nuovaImmagine);
      const downloadURL = await getDownloadURL(fileRef);
      
      await addDoc(collection(db, "banners_promozionali"), {
        localeTag: localeTag,
        titolo: nuovoTitolo,
        link: nuovoLink,
        imageUrl: downloadURL,
        storagePath: filePath,
        timestamp: new Date(),
        attivo: true
      });
      
      setSnackbar("✅ Banner caricato con successo!");
      setNuovoTitolo("");
      setNuovoLink("");
      setNuovaImmagine(null);
      setShowForm(false);
      setFormKey(prev => prev + 1);
      
      await ricaricaBanners();
      setUploading(false);
      setTimeout(() => setSnackbar(""), 3000);
    } catch (err) {
      console.error("Errore nel caricamento banner:", err);
      setSnackbar("❌ Errore nel caricamento");
      setTimeout(() => setSnackbar(""), 3000);
      setUploading(false);
    }
  };

  // Attiva/Disattiva banner
  const toggleAttivo = async (id, attivoCorrente) => {
    try {
      await updateDoc(doc(db, "banners_promozionali", id), { attivo: !attivoCorrente });
      setSnackbar(attivoCorrente ? "⏸️ Banner disattivato" : "✅ Banner attivato");
      setTimeout(() => setSnackbar(""), 3000);
      await ricaricaBanners();
    } catch (err) {
      console.error("Errore nell'aggiornamento:", err);
    }
  };

  // Elimina banner
  const eliminaBanner = async (id, storagePath) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo banner?")) return;
    
    try {
      await deleteDoc(doc(db, "banners_promozionali", id));
      
      if (storagePath) {
        try {
          const storage = getStorage();
          const fileRef = ref(storage, storagePath);
          await deleteObject(fileRef);
        } catch (err) {
          console.error("Errore nell'eliminazione file:", err);
        }
      }
      
      setSnackbar("🗑️ Banner eliminato");
      setTimeout(() => setSnackbar(""), 3000);
      await ricaricaBanners();
    } catch (err) {
      console.error("Errore nell'eliminazione:", err);
      setSnackbar("❌ Errore nell'eliminazione");
      setTimeout(() => setSnackbar(""), 3000);
    }
  };

  // Apri modalità modifica
  const apriModifica = (banner) => {
    setBannerInModifica(banner);
    setNuovoTitolo(banner.titolo);
    setNuovoLink(banner.link);
    
    setModalitaModifica(true);
    setShowForm(true);
  };

  // Chiudi modalità modifica
  const chiudiModifica = () => {
    setModalitaModifica(false);
    setBannerInModifica(null);
    setNuovoTitolo("");
    setNuovoLink("");
    setNuovaImmagine(null);
    setShowForm(false);
    setFormKey(prev => prev + 1);
  };

  // Salva modifiche banner
  const salvaModifiche = async () => {
    if (!nuovoTitolo.trim()) {
      setSnackbar("⚠️ Inserisci un titolo!");
      setTimeout(() => setSnackbar(""), 3000);
      return;
    }

    setUploading(true);

    try {
      // Aggiorna i campi del documento
      await updateDoc(doc(db, "banners_promozionali", bannerInModifica.id), {
        titolo: nuovoTitolo,
        link: nuovoLink
      });

      // Se è stata caricata una nuova immagine, sostituiscila
      if (nuovaImmagine) {
        const storage = getStorage();
        const filename = nuovaImmagine.name;
        const timestamp = Date.now();
        const filePath = `banners_promozionali/${localeTag}_${timestamp}_${filename}`;
        const fileRef = ref(storage, filePath);

        await uploadBytes(fileRef, nuovaImmagine);
        const downloadURL = await getDownloadURL(fileRef);

        await updateDoc(doc(db, "banners_promozionali", bannerInModifica.id), {
          imageUrl: downloadURL,
          storagePath: filePath
        });

        // Elimina la vecchia immagine
        if (bannerInModifica.storagePath) {
          try {
            const oldFileRef = ref(storage, bannerInModifica.storagePath);
            await deleteObject(oldFileRef);
          } catch (err) {
            console.error("Errore nell'eliminazione vecchia immagine:", err);
          }
        }
      }

      setSnackbar("✅ Banner modificato con successo!");
      chiudiModifica();
      await ricaricaBanners();
      setUploading(false);
      setTimeout(() => setSnackbar(""), 3000);
    } catch (err) {
      console.error("Errore nella modifica banner:", err);
      setSnackbar("❌ Errore nella modifica");
      setTimeout(() => setSnackbar(""), 3000);
      setUploading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="page-with-navbar gbp-page">
        <div className="gbp-header">
          <div>
  <div className="gbp-badge">
    PROMO & ADV
  </div>

  <h1 className="gbp-title">
    Gestione Banner
  </h1>

  <p className="gbp-subtitle">
    Gestisci i banner promozionali visibili nel MenuView.
  </p>
</div>
          <button
            onClick={() => {
              setModalitaModifica(false);
              setBannerInModifica(null);
              setNuovoTitolo("");
              setNuovoLink("");
              setNuovaImmagine(null);
              setShowForm(!showForm);
              setFormKey(prev => prev + 1);
            }}
            className="gbp-main-button"
          >
            {showForm ? "❌ Chiudi" : "➕ Nuovo Banner"}
          </button>
        </div>

        {snackbar && (
  <div
    className={`gbp-snackbar ${
      snackbar.includes("✅")
        ? "success"
        : snackbar.includes("⚠️")
        ? "warning"
        : "error"
    }`}
  >
    {snackbar}
  </div>
)}

        {/* Form caricamento nuovo banner */}
        {showForm && (
          <form
  key={formKey}
  onSubmit={
    modalitaModifica
      ? (e) => {
          e.preventDefault();
          salvaModifiche();
        }
      : caricaBanner
  }
  className="gbp-form-card"
>
            <h3 className="gbp-form-title">
              {modalitaModifica ? "✏️ Modifica Banner Promozionale" : "📤 Carica Nuovo Banner Promozionale"}
            </h3>
            
            <div style={{ marginBottom: "1rem" }}>
              <label className="gbp-label">
                📝 Titolo Banner *
              </label>
              <input
                type="text"
                value={nuovoTitolo}
                onChange={(e) => setNuovoTitolo(e.target.value)}
                placeholder="Es. Nuova offerta speciale"
                required
                className="gbp-input"
              />
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label className="gbp-label">
                🔗 Link (opzionale)
              </label>
              <input
                type="url"
                value={nuovoLink}
                onChange={(e) => setNuovoLink(e.target.value)}
                placeholder="https://esempio.com/offerta"
                className="gbp-input"
              />
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label className="gbp-label">
                🖼️ Immagine Banner {modalitaModifica ? "(opzionale - lascia vuoto per mantenere attuale)" : "* (consigliato: 1200x300px)"}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setNuovaImmagine(e.target.files[0])}
                required={!modalitaModifica}
                className="gbp-file-input"
              />
              {modalitaModifica && bannerInModifica && (
                <div className="gbp-current-image">
                  📷 Immagine attuale: {bannerInModifica.titolo}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={uploading}
              className={`gbp-save-button ${uploading ? "disabled" : ""}`}
            >
              {uploading ? "⏳ Elaborazione..." : modalitaModifica ? "💾 Salva Modifiche" : "💾 Salva Banner"}
            </button>
          </form>
        )}

        {/* Lista banner esistenti */}
        {loading ? (
          <p className="gbp-loading">
            🔄 Caricamento banner...
          </p>
        ) : banners.length === 0 ? (
          <div className="gbp-empty">
            <p className="gbp-empty-title">
              📭 Nessun banner caricato
            </p>
            <p className="gbp-empty-subtitle">
              Clicca su "Nuovo Banner" per iniziare
            </p>
          </div>
        ) : (
          <div className="gbp-banner-list">
            {banners.map((item) => (
              <div
  key={item.id}
  className={`gbp-banner-card ${
    item.attivo ? "active" : "inactive"
  }`}
>
                <div className="gbp-banner-content">
                  <img
                    src={item.imageUrl}
                    alt={item.titolo}
                    className="gbp-banner-image"
                  />
                  
                  <div className="gbp-banner-info">
                    <h3 style={{ margin: "0 0 0.5rem 0", color: "#333" }}>
                      {item.titolo}
                    </h3>
                    {item.link && (
                      <p style={{ margin: "0.3rem 0", fontSize: "0.9rem", color: "#666" }}>
                        🔗 <a href={item.link} target="_blank" rel="noopener noreferrer" 
                            style={{ color: "#FF6F00" }}>
                          {item.link}
                        </a>
                      </p>
                    )}
                    <p style={{ margin: "0.3rem 0", fontSize: "0.85rem", color: "#999" }}>
                      ⏱️ {item.timestamp?.seconds 
                        ? new Date(item.timestamp.seconds * 1000).toLocaleDateString()
                        : "Data non disponibile"}
                    </p>
                    <p style={{ 
                      margin: "0.5rem 0 0 0", 
                      fontSize: "0.9rem", 
                      fontWeight: "600",
                      color: item.attivo ? "#4CAF50" : "#f44336"
                    }}>
                      {item.attivo ? "✅ Attivo" : "⏸️ Disattivato"}
                    </p>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <button
                      onClick={() => apriModifica(item)}
                      style={{
                        backgroundColor: "#2196F3",
                        color: "white",
                        border: "none",
                        padding: "0.5rem 1rem",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        fontWeight: "600"
                      }}
                    >
                      ✏️ Modifica
                    </button>
                    <button
                      onClick={() => toggleAttivo(item.id, item.attivo)}
                      style={{
                        backgroundColor: item.attivo ? "#FF9800" : "#4CAF50",
                        color: "white",
                        border: "none",
                        padding: "0.5rem 1rem",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        fontWeight: "600"
                      }}
                    >
                      {item.attivo ? "⏸️ Disattiva" : "▶️ Attiva"}
                    </button>
                    <button
                      onClick={() => eliminaBanner(item.id, item.storagePath)}
                      style={{
                        backgroundColor: "#f44336",
                        color: "white",
                        border: "none",
                        padding: "0.5rem 1rem",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        fontWeight: "600"
                      }}
                    >
                      🗑️ Elimina
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default GestioneBannerPromozionale;
